/** @jsx jsx */
import { jsx, useTheme } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';

import { Grid, Link, Typography2 } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import { isSearchTheme } from 'bundles/search-common/searchTheme';
import { getLessonsVideoObjectUrl } from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/search-common';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps<'a'>>(Link);

type PropsFromCaller = {
  parentCourseName: string;
  parentCourseId: string;
  parentLessonName?: string;
  parentLessonObjectUrl?: string;
  entityType: string;
  objectID?: string;
};

type PropsFromRouter = {
  router: InjectedRouter;
};

type Props = PropsFromCaller & PropsFromRouter;

const ShortFormContentBreadcrumb = ({
  parentCourseName,
  parentCourseId,
  parentLessonName,
  parentLessonObjectUrl,
  entityType,
  objectID,
  router,
}: Props) => {
  const theme = useTheme();
  if (!isSearchTheme(theme)) {
    throw new Error(
      'ProductInfo can only be used in the context of a SearchTheme. Wrap this component with a ThemeProvider.'
    );
  }

  const onCourseClick = (e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    launchMiniModal(router, parentCourseId, true, undefined);
  };

  const videoId = entityType === 'VIDEO' ? getLessonsVideoObjectUrl(parentCourseId, parentLessonObjectUrl) : undefined;

  return (
    <div css={theme.shortFormContentBreadcrumb}>
      <Grid container spacing={4}>
        <div>
          <div>
            <Typography2 component="span" variant="bodyPrimary" color="supportText" className="product-name">
              {_t('From the course:')}
            </Typography2>
            <TrackedLink
              className="product-name-link"
              {...Link.defaultProps}
              onClick={onCourseClick}
              variant="standard"
              typographyVariant="bodyPrimary"
              trackingName="short_form_content_course_breadcrumb_link"
              trackingData={{ courseId: parentCourseId, objectID }}
              href="#"
            >
              {parentCourseName}
            </TrackedLink>
            {parentLessonName && (
              <React.Fragment>
                <span className="sfc-breadcrumb-lesson-container">
                  {parentLessonName && <ChevronNextIcon size="small" className="check-icon" />}
                  <Typography2 component="span" variant="bodyPrimary" color="supportText" className="product-name">
                    {_t(' From the lesson:')}
                  </Typography2>
                  <TrackedLink
                    className="product-name-link"
                    {...Link.defaultProps}
                    variant="standard"
                    typographyVariant="bodyPrimary"
                    trackingName="short_form_content_lesson_breadcrumb_link"
                    trackingData={{ courseId: parentCourseId, videoId }}
                    href={parentLessonObjectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e?.stopPropagation()}
                  >
                    {parentLessonName}
                  </TrackedLink>
                </span>
              </React.Fragment>
            )}
          </div>
        </div>
      </Grid>
    </div>
  );
};

export default compose<Props, PropsFromCaller>(
  connectToRouter((router) => ({
    router,
  }))
)(ShortFormContentBreadcrumb);
